/**
 * Where everything in the three cards is, as pure functions of the story
 * clock — the web's version of home-ceo-mobile `wee-poses.ts` and the
 * choreography in `wee-sequence.tsx` (named `eee-*` at main @ 8d85fd1).
 *
 * The app tells the story with ONE house in one card: it becomes a
 * lantern (Wish), splits into five measured options and one is
 * picked (Explore), and the pick comes alive in a sunrise (Enact). The
 * site lays the three beats side by side, one card each, so each card
 * performs its own beat while the clock is in it, on the app's cues and
 * easings, and rests as the mock draws it otherwise.
 *
 * Every card draws the same picture at every width: three across it is
 * the card's top half; stacked, the current card opens to show it and
 * the others fold to their house and name.
 */

import { CHOSEN, TINT_PEAK } from './wee-palette';
import {
  ALIVE_AT,
  BEAT_WINDOW,
  CUE,
  OMEGA,
  SELECTION_AT,
  bell,
  clamp01,
  enter,
  glide,
  pop,
  seeded,
  type LoopBeat,
} from './wee-timeline';

/**
 * The art box every card draws in, in its own units. Houses are sized
 * by the width of their INK (`HOUSE_W` in house-mark.ts is 100).
 */
export const ART = { w: 300, h: 190, cx: 150, cy: 118, ink: 88, grow: 1.2, lift: 10, glint: [11, 17] } as const;

/** The mark's ink is 86.3% of the width of the square the app draws it in; halos and bursts are sized from that square. */
export const INK_SHARE = 0.863;

/** How long a card takes to rise at the start of its beat, and to settle at the end. */
const CARD_RISE_S = 0.7;
const CARD_FALL_S = 0.7;

// The app's Explore constants.
/** When the options take on their own colours, and the tint lets go. */
export const OWN_FROM = CUE.explore + 1.0;
export const OWN_TO = CUE.explore + 2.2;
/** The pick is the winning column growing; the others recede. */
export const HOUSE_EMPHASIS = 1.28;
export const MEASUREMENT_EMPHASIS = 1.18;
export const RECEDED_OPACITY = 0.3;

/** Reduced motion, and the screenshot suite, hold one frame: just after the pick. */
export const STILL_T = SELECTION_AT + 0.9;

/** Where the Explore card places its five options. */
export const EVAL = {
  pitch: 58,
  rowY: 60,
  ink: 44,
  barFoot: 124,
  barW: 5.5,
  barWChosen: 7,
  barGap: 3,
  barMaxH: 24,
  barMaxHChosen: 32,
  pieY: 141,
  pieR: 7.5,
  pieRChosen: 9,
} as const;

export type HousePose = { cx: number; cy: number; ink: number };
export type GlintPose = { x: number; y: number; span: number; rotation: number; opacity: number };
export type BarPose = { x: number; y: number; w: number; h: number };

/**
 * How lifted a card is: 0 at rest, 1 while the clock is in its beat —
 * rising over the beat's first moments and settling over its last, the
 * shape the app's words light and dim with. It is 0 at both ends of the
 * beat, so the wrap from `loop` to `wish` changes nothing.
 */
export function activeness(t: number, beat: LoopBeat): number {
  const { from, to } = BEAT_WINDOW[beat];
  return Math.min(glide(t, 0, 1, from, from + CARD_RISE_S), glide(t, 1, 0, to - CARD_FALL_S, to));
}

/**
 * Whether a card is open: from the first instant of its beat to the
 * last. Stacked, the open card unfolds to show its picture and the
 * others fold to their house and name; the fold itself is CSS's, so
 * the card closing and the next one opening cross at the boundary.
 */
export function isOpen(t: number, beat: LoopBeat): boolean {
  const { from, to } = BEAT_WINDOW[beat];
  return t >= from && t < to;
}

/** How far the options' colour has drifted across a mark, in mark widths: two turns per cycle, like the app's tint sweep. */
export function tintShift(t: number): number {
  return 0.8 * Math.sin(t * 2 * OMEGA);
}

/** The glints that drift round a lantern: whole turns per cycle, so they are where they were at the wrap. */
function glints(
  t: number,
  house: HousePose,
  span: readonly [number, number],
  count: number,
  strength: number,
): GlintPose[] {
  return Array.from({ length: count }, (_, i) => {
    const s = (k: number) => seeded(i + 40, k);
    const twinkleTurns = 2 + Math.round(s(7) * 2);
    const twinkle = 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(t * twinkleTurns * OMEGA + i));
    return {
      x: house.cx + (s(1) - 0.5) * house.ink * 1.9 + 3 * Math.sin(t * OMEGA + 6.28 * s(5)),
      y: house.cy + (s(2) - 0.5) * house.ink * 1.3 - 6 * Math.sin(t * OMEGA + 6.28 * s(8)),
      span: span[0] + (span[1] - span[0]) * s(3),
      rotation: t * OMEGA + 3 * s(6),
      opacity: strength * twinkle * 0.9,
    };
  });
}

// --- Wish ------------------------------------------------------------

/**
 * The white house becomes a lantern: a pool of depth blooms under it and
 * a halo rim around it, glints drift, and toward the end the options'
 * colour drifts across it — the wish, before it has a shape.
 */
export function wishPose(t: number) {
  const g = ART;
  const a = activeness(t, 'wish');
  const lantern =
    glide(t, 0, 1, CUE.wish + 0.1, CUE.wish + 1.6) *
    glide(t, 1, 0, CUE.explore + 0.2, CUE.explore + 1.3);
  const ink = g.ink * (1 + (g.grow - 1) * a) * (1 + 0.06 * lantern);
  const float = 0.012 * g.ink * Math.sin((t - CUE.wish) * 0.85) * lantern;
  const house = { cx: g.cx, cy: g.cy - g.lift * a + float, ink };
  const box = ink / INK_SHARE;
  const halo =
    glide(t, 0, 1, CUE.wish, CUE.wish + 1.2) *
    glide(t, 1, 0, CUE.explore + 0.25, CUE.explore + 1.0);
  return {
    a,
    house,
    lantern,
    poolR: box * 1.25,
    halo,
    haloR: box * 0.9 * (1 + 0.05 * Math.sin((t - CUE.wish) * 1.3)),
    tint:
      TINT_PEAK *
      glide(t, 0, 1, CUE.explore - 1.8, CUE.explore - 0.3) *
      glide(t, 1, 0, CUE.explore + 0.25, CUE.explore + 1.0),
    glints: glints(t, house, g.glint, 4, lantern),
  };
}

// --- Explore ---------------------------------------------------------

/** Each option's share of its pie: deep, pale, white. The winner's deep slice is the biggest. */
export function pieShares(index: number): [number, number, number] {
  const chosen = index === CHOSEN;
  const deep = chosen ? 0.62 : 0.3 + 0.2 * seeded(index + 5, 1);
  const pale = chosen ? 0.26 : 0.25 + 0.2 * seeded(index + 5, 2);
  return [deep, pale, 1 - deep - pale];
}

/** How far each bar of an option reaches toward its maximum. The winner measures better — the reason it wins. */
function barFraction(index: number, bar: number): number {
  return index === CHOSEN ? 0.72 + 0.28 * seeded(index + 7, bar) : 0.28 + 0.42 * seeded(index + 1, bar);
}

function barHeight(t: number, index: number, bar: number, maxH: number): number {
  const risesAt = CUE.explore + 2.0 + index * 0.1 + bar * 0.14;
  const rise = glide(t, 0, 1, risesAt, risesAt + 0.85);
  const breath = 1 + 0.1 * Math.sin(t * 1.3 + bar * 1.9 + index * 1.1);
  return Math.max(0.5, Math.min(maxH, maxH * barFraction(index, bar) * rise * breath));
}

/** The measurements' envelope: in as they are drawn, the also-rans recede at the pick, all gone as Enact begins. */
function measurementOpacity(t: number, index: number): number {
  return (
    enter(t, CUE.explore + 1.95 + index * 0.1, 0.6) *
    (index === CHOSEN ? 1 : glide(t, 1, RECEDED_OPACITY, SELECTION_AT, SELECTION_AT + 0.6)) *
    glide(t, 1, 0, CUE.enact - 0.5, CUE.enact + 0.1)
  );
}

/**
 * The resting house splits into five options that fan out
 * into a row, light at first under the drifting colour, then each in its
 * own family; three brush bars and a small pie rise under each; at the
 * pick the amber column grows and the others recede. As the beat ends
 * they go and the resting house returns.
 */
export function explorePose(t: number) {
  const g = ART;
  const e = EVAL;
  const a = activeness(t, 'explore');
  const restInk = g.ink * (1 + (g.grow - 1) * a);
  const restCy = g.cy - g.lift * a;
  const rest = clamp01(
    1 -
      glide(t, 0, 1, CUE.explore + 0.25, CUE.explore + 1.0) +
      glide(t, 0, 1, CUE.enact + 0.1, CUE.enact + 0.9),
  );
  const own = glide(t, 0, 1, OWN_FROM, OWN_TO);
  const tint = TINT_PEAK * glide(t, 1, 0, OWN_FROM + 0.2, OWN_TO + 0.1);
  const pick = pop(t, SELECTION_AT, 0.7);
  const leave = glide(t, 1, 0, CUE.enact - 0.25, CUE.enact + 0.45);

  const candidates = Array.from({ length: 5 }, (_, i) => {
    const chosen = i === CHOSEN;
    const from = CUE.explore + 0.15 + i * 0.11;
    const p = glide(t, 0, 1, from, from + 1.05);
    let ink = restInk + (e.ink - restInk) * p;
    let cy = restCy + (e.rowY - restCy) * p + 1.5 * Math.sin(t * 1.25 + i * 1.7) * p;
    if (chosen) {
      ink *= 1 + (HOUSE_EMPHASIS - 1) * pick;
      cy -= 4 * glide(t, 0, 1, SELECTION_AT, SELECTION_AT + 0.5);
    } else {
      ink *= glide(t, 1, 0.88, SELECTION_AT, SELECTION_AT + 0.8);
    }
    const opacity =
      enter(t, from, 0.5) *
      (chosen
        ? glide(t, 1, 0, CUE.enact + 0.05, CUE.enact + 0.75)
        : glide(t, 1, RECEDED_OPACITY, SELECTION_AT, SELECTION_AT + 0.8) * leave);
    return { house: { cx: g.cx + (i - 2) * e.pitch * p, cy, ink }, opacity };
  });

  // The column grows about its own middle, between the bars and the pie.
  const pivotY = (e.barFoot - e.barMaxHChosen + e.pieY) / 2;
  const columns = Array.from({ length: 5 }, (_, i) => {
    const chosen = i === CHOSEN;
    const slotX = g.cx + (i - 2) * e.pitch;
    const w = chosen ? e.barWChosen : e.barW;
    const maxH = chosen ? e.barMaxHChosen : e.barMaxH;
    const rowW = 3 * w + 2 * e.barGap;
    const bars: BarPose[] = [0, 1, 2].map((b) => {
      const h = barHeight(t, i, b, maxH);
      return { x: slotX - rowW / 2 + b * (w + e.barGap), y: e.barFoot - h, w, h };
    });
    const shares = pieShares(i);
    const drawsAt = CUE.explore + 3.0 + i * 0.12;
    let start = 0;
    const slices = shares.map((share, k) => {
      const slice = { start, sweep: share * enter(t, drawsAt + k * 0.18, 0.7) };
      start += share;
      return slice;
    });
    return {
      opacity: measurementOpacity(t, i),
      scale: chosen ? 1 + (MEASUREMENT_EMPHASIS - 1) * pick : 1,
      slotX,
      pivotY,
      bars,
      pie: {
        cx: slotX,
        cy: e.pieY,
        r: (chosen ? e.pieRChosen : e.pieR) * (1 + 0.04 * Math.sin(t * 2 * OMEGA + i)),
        slices,
      },
    };
  });

  return { a, rest: { house: { cx: g.cx, cy: restCy, ink: restInk }, opacity: rest }, own, tint, candidates, columns };
}

// --- Enact -----------------------------------------------------------

/**
 * The gold house comes alive: lit from within with a burst, a glow and
 * a pass of shimmer, breathing, with sparkles drifting out; then it
 * lightens and lightens to white as the page's sunrise drains, and
 * takes its gold back once the next Wish has begun.
 *
 * `firstPass` is true until the clock first wraps: before its first
 * Enact the house has never been white, so it has nothing to return from.
 */
export function enactPose(t: number, firstPass: boolean) {
  const g = ART;
  const a = activeness(t, 'enact');
  const alive = enter(t, ALIVE_AT, 0.5) * glide(t, 1, 0, CUE.loop - 1.7, CUE.loop - 0.7);
  const breathe = 1 + 0.03 * Math.sin(Math.max(0, t - ALIVE_AT) * 2.2) * alive;
  const ink = g.ink * (1 + (g.grow - 1) * a) * breathe;
  const house = { cx: g.cx, cy: g.cy - g.lift * a, ink };
  const box = ink / INK_SHARE;

  // Along [rest gold, lit gold, white].
  let phase: number;
  if (t >= CUE.enact) {
    phase =
      glide(t, 0, 1, ALIVE_AT - 0.25, ALIVE_AT + 0.4) + glide(t, 0, 1, ALIVE_AT + 1.2, CUE.loop - 0.05);
  } else {
    phase = firstPass ? 0 : 2 * (1 - glide(t, 0, 1, CUE.wish + 0.3, CUE.wish + 1.8));
  }

  const burstFrom = ALIVE_AT - 0.15;
  const burstP = enter(t, burstFrom, 1.1);
  const sparkleLife = enter(t, ALIVE_AT, 0.5) * glide(t, 1, 0, CUE.loop - 1.6, CUE.loop - 0.6);

  return {
    a,
    house,
    phase,
    burst: {
      r: box * (0.5 + 1.1 * burstP),
      opacity: t >= burstFrom && t <= burstFrom + 1.1 ? Math.pow(1 - burstP, 1.3) : 0,
    },
    glow: enter(t, ALIVE_AT - 0.35, 0.7) * glide(t, 1, 0, ALIVE_AT + 1.5, CUE.loop - 1.3),
    shimmer: glide(t, 0, 1, ALIVE_AT - 0.2, ALIVE_AT + 1.4),
    sparkles: Array.from({ length: 6 }, (_, i): GlintPose => {
      const phaseI = (Math.max(0, t - ALIVE_AT) * (0.4 + seeded(i, 11) * 0.4) + seeded(i, 12)) % 1;
      const angle = seeded(i, 13) * Math.PI * 2 + phaseI * 0.6;
      const radius = box * (0.55 + seeded(i, 14) * 0.35) + phaseI * box * 0.125;
      return {
        x: house.cx + Math.cos(angle) * radius,
        y: house.cy + Math.sin(angle) * radius * 0.8,
        span: box * (0.075 + 0.05 * seeded(i, 15)),
        rotation: phaseI * Math.PI,
        opacity: sparkleLife * bell(phaseI) * 0.95,
      };
    }),
  };
}
